import { type FC } from "react";
import { useLocation, useMatch } from "react-router";

import { LoadingSpinner, Tag } from "widgets";
import { toSafeInt } from "widgets/utils";

import { GenomeView, type IGenomeViewOwnProps } from "../genome-viewer/GenomeView.js";
import { getHitHitRange, getHspHitRange } from "@/app-utils/blast.ts";
import { getEventTagIntent } from "@/app-utils/events.ts";
import { getPaddedRange } from "@/app-utils/range.ts";
import { type BlastHit, type BlastJob, type Contig, type JobResult, type View } from "@/graphql.ts";
import { getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.ts";
import { SEQUENCE_TYPE, TRACK_TYPE } from "@/settings/settings.js";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

export interface IBlastResultGenomeViewProps {
  job: BlastJob;
  isLoading: boolean;
  jobResult?: JobResult;
  hitNum?: number;
  view: View;
}

const BlastResultGenomeView: FC<IBlastResultGenomeViewProps> = (props) => {
  const location = useLocation();
  const hspNum = useMatch("/job/:jobId/results/:queryId/hitdetails/:hitNum/genomeview/:hspNum")?.params.hspNum;
  if (!props.jobResult || props.isLoading) {
    return <LoadingSpinner inCenter />;
  }

  // Render empty content if no hitNum is provided explicitly
  const hit: BlastHit = props.jobResult.blastHits[props.hitNum ? props.hitNum - 1 : 0];
  if (hit.target.type.name !== SEQUENCE_TYPE.GENOMIC || hit.target.organism.id === 0) {
    return <ContentPlaceholder text="No genome view available" />;
  }

  // Fake Blast tags. See BlastPageHeader.
  const headerTags = [
    <Tag
      label={props.job.status}
      intentColor={getEventTagIntent(props.job.status)}
      key={"blast-result-hit-summary-tag-1"}
    />,
    props.job.tool ? <Tag label={props.job.tool.toUpperCase()} key={"blast-result-hit-summary-tag-2"} /> : null,
  ];

  const safeHspNum = toSafeInt(hspNum);
  let range = safeHspNum ? getHspHitRange(hit.hsps[safeHspNum - 1]) : getHitHitRange(hit);
  // apply the range stored in the view if the URLs match (hit and/or HSP)
  if (props.view && location.pathname === props.view.params) {
    range = props.view.state.genomeView.range;
  }

  const contig: Contig = hit.target as unknown as Contig;
  const genomeViewProps: IGenomeViewOwnProps = {
    jobResult: props.jobResult,
    genomeId: contig.organism.id,
    contigId: contig.id,
    range: getPaddedRange(range, contig.length),
    genomes: [contig.organism],
    defaultTracks: getClassicGenomeBrowserTracks([TRACK_TYPE.SEQUENCE, TRACK_TYPE.GENE, TRACK_TYPE.BLAST_ALIGNMENT]),
    headerTags: headerTags,
    title: `Hit ${props.hitNum}`,
    subtitle: "Genome Viewer",
  };

  return <GenomeView {...genomeViewProps} />;
};

export { BlastResultGenomeView };
